import { CardResponseMeta, List } from '@yleisradio/areena-types';
import { useSnowplow } from 'contexts/SnowplowContext';
import { useMemo } from 'react';
import { trackClick } from 'services/snowplowAnalytics/snowplowAnalytics';
import { randomUUID } from 'utils/uuid';
import { GroupEngagementData } from './types';
import { useCookieConsent } from 'contexts/CookieConsentContext';

export function useListEngagementData({
  cardPageMeta: meta,
  isPaginated,
  list,
  listIndex,
}: {
  cardPageMeta: CardResponseMeta | undefined;
  isPaginated: boolean;
  list: List | undefined;
  listIndex: number | undefined;
}): GroupEngagementData {
  const consent = useCookieConsent();

  const length = meta?.count ?? null;
  const {
    source_ref,
    source_url,
    recommendation_id,
    recommendation_method,
    ab_test_name,
    ab_test_group,
  } = meta?.analytics?.context?.yle || {};

  const { title: label, style } = list || {};

  const hasDevelopmentOrPersonalizationConsent =
    consent?.development || consent?.personalization;

  return useMemo(
    (): GroupEngagementData => ({
      isPaginated,
      label,
      length,
      index: listIndex,
      referer: source_ref,
      recommendation: {
        id: hasDevelopmentOrPersonalizationConsent
          ? recommendation_id
          : undefined,
        method: hasDevelopmentOrPersonalizationConsent
          ? recommendation_method
          : undefined,
      },
      abTest: {
        name: hasDevelopmentOrPersonalizationConsent
          ? ab_test_name || ''
          : undefined,
        group: hasDevelopmentOrPersonalizationConsent
          ? ab_test_group || ''
          : undefined,
      },
      url: source_url,
      style,
    }),
    [
      hasDevelopmentOrPersonalizationConsent,
      isPaginated,
      label,
      length,
      listIndex,
      recommendation_id,
      recommendation_method,
      ab_test_name,
      ab_test_group,
      source_ref,
      source_url,
      style,
    ]
  );
}

export function useTrackCardClick(cardViewUuid: string): {
  trackCardClick: (() => void) | undefined;
  trackPlayButtonClick: (() => void) | undefined;
} {
  const { isSnowplowReady } = useSnowplow();

  if (!isSnowplowReady) {
    return {
      trackCardClick: undefined,
      trackPlayButtonClick: undefined,
    };
  }

  return {
    trackCardClick: () => {
      trackClick({ action: 'link', viewUuid: cardViewUuid });
    },
    trackPlayButtonClick: () => {
      trackClick({ action: 'play', viewUuid: cardViewUuid });
    },
  };
}

export function useViewUuid(): string {
  return useMemo(() => randomUUID(), []);
}
